import React, { useState, useEffect, useRef } from 'react';
import { Box, TextField, Button, IconButton } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

interface JsonViewerProps {
  value: string | object;
  editable?: boolean;
  height?: string | number;
  maxLines?: number;
  truncateLines?: number;
  onChange?: (value: string) => void;
  showExpandButton?: boolean;
}

export const JsonViewer: React.FC<JsonViewerProps> = ({
  value,
  editable = false,
  height = "400px",
  maxLines,
  truncateLines = 10,
  onChange,
  showExpandButton = false
}) => {
  const [lineNumbers, setLineNumbers] = useState<string>("1");
  const [isExpanded, setIsExpanded] = useState(false);
  const [copied, setCopied] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const lineNumbersRef = useRef<HTMLDivElement>(null);

  // Convert object to string if needed and remove trailing newlines
  const rawStringValue = typeof value === 'string' ? value : JSON.stringify(value, null, 2);
  const stringValue = rawStringValue.replace(/\n+$/, '');

  useEffect(() => {
    if (stringValue) {
      const lines = stringValue.split("\n").length;
      setLineNumbers(Array.from({ length: lines }, (_, i) => i + 1).join("\n"));
    } else {
      setLineNumbers("1");
    }
  }, [stringValue]);

  const handleScroll = () => {
    if (textareaRef.current && lineNumbersRef.current) {
      lineNumbersRef.current.scrollTop = textareaRef.current.scrollTop;
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    onChange?.(newValue);
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(stringValue);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  if (editable) {
    return (
      <Box
        sx={{
          position: "relative",
          display: "flex",
          height: height,
          border: "1px solid #ccc",
          borderRadius: "4px",
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '0.5rem',
            right: '0.5rem',
            zIndex: 1,
            backgroundColor: 'white',
            borderRadius: '4px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}
        >
          <IconButton
            size="small"
            onClick={handleCopy}
            sx={{
              color: copied ? '#4caf50' : '#666',
              '&:hover': {
                backgroundColor: 'rgba(0,0,0,0.04)'
              }
            }}
            title={copied ? 'Copied!' : 'Copy to clipboard'}
          >
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        </Box>
        <Box
          ref={lineNumbersRef}
          sx={{
            width: "40px",
            padding: "8px",
            backgroundColor: "#f5f5f5",
            borderRight: "1px solid #ccc",
            overflow: "hidden",
            fontFamily: "monospace",
            fontSize: "14px",
            lineHeight: "20px",
            color: "#666",
            userSelect: "none",
          }}
        >
          {lineNumbers}
        </Box>
        <TextField
          inputRef={textareaRef}
          multiline
          fullWidth
          value={stringValue}
          onChange={handleChange}
          onScroll={handleScroll}
          sx={{
            "& .MuiInputBase-root": {
              height: "100%",
              fontFamily: "monospace",
              fontSize: "14px",
              lineHeight: "20px",
              padding: "8px",
            },
            "& .MuiInputBase-input": {
              height: "100% !important",
              overflow: "auto !important",
            },
          }}
          InputProps={{
            disableUnderline: true,
          }}
        />
      </Box>
    );
  }

  // Read-only version with optional truncation
  const lines = stringValue.split('\n');
  const isTruncated = maxLines && lines.length > maxLines && !isExpanded;
  const linesToRender = isTruncated ? lines.slice(0, truncateLines) : lines;

  return (
    <Box>
      <Box 
        sx={{ 
          position: 'relative',
          display: 'flex', 
          width: '100%',
          border: "1px solid #ddd",
          borderRadius: "4px",
          overflow: "hidden"
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '0.5rem',
            right: '0.5rem',
            zIndex: 1,
            backgroundColor: 'white',
            borderRadius: '4px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}
        >
          <IconButton
            size="small"
            onClick={handleCopy}
            sx={{
              color: copied ? '#4caf50' : '#666',
              '&:hover': {
                backgroundColor: 'rgba(0,0,0,0.04)'
              }
            }}
            title={copied ? 'Copied!' : 'Copy to clipboard'}
          >
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        </Box>
        <Box sx={{ 
          padding: '0.5rem 1rem',
          borderRight: '1px solid #ddd',
          color: '#666',
          userSelect: 'none',
          fontFamily: 'monospace',
          fontSize: '0.875rem',
          lineHeight: 1.5,
          backgroundColor: '#f5f5f5',
          minWidth: '50px',
          textAlign: 'right'
        }}>
          {linesToRender.map((_, index) => (
            <div key={index}>{index + 1}</div>
          ))}
        </Box>
        <Box sx={{ 
          padding: '0.5rem 1rem',
          flex: 1,
          fontFamily: 'monospace',
          fontSize: '0.875rem',
          lineHeight: 1.5,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          overflowX: 'auto',
          backgroundColor: 'white'
        }}>
          {linesToRender.map((line, index) => (
            <div key={index}>{line}</div>
          ))}
          {isTruncated && (
            <Box sx={{ 
              p: 1,
              mt: 1,
              borderTop: '1px dashed #ddd',
              color: '#666',
              fontStyle: 'italic'
            }}>
              ... {lines.length - truncateLines} more lines
            </Box>
          )}
        </Box>
      </Box>
      {(isTruncated || (showExpandButton && lines.length > truncateLines)) && (
        <Box sx={{ mt: 1, textAlign: 'center' }}>
          <Button 
            size="small" 
            variant="outlined" 
            onClick={() => setIsExpanded(!isExpanded)}
          >
            {isExpanded ? 'Collapse' : `Show all ${lines.length} lines`}
          </Button>
        </Box>
      )}
    </Box>
  );
}; 